import Head from 'next/head'
import { FivePound } from 'features/ECommerce/screens/ProductDetail/FivePound'
import { prefetchProductVariantDetail } from 'features/ECommerce/api/queries/useFetchProductVariantDetail'
import { GetServerSidePropsContext } from 'next'
import { ProductVariantDetail as ProductDetailType } from 'models/Product'
import { useTranslation } from 'hooks/useTranslation'
import { Essentials } from 'features/ECommerce/screens/ProductDetail/Essentials'
import { Startr } from 'features/ECommerce/screens/ProductDetail/Startr'
import { Treats } from 'features/ECommerce/screens/ProductDetail/Treats'
import { prefetchShoppingCart } from 'features/ECommerce/api/queries/useFetchShoppingCart'
import Cookies from 'cookies'
import { CookieKeys } from 'services/Cookies'
import { sessionCookieName } from 'pages/api/auth/_cookieHelpers'
import { getPayloadFromJWT } from 'utils/jwt'
import Script from 'next/script'
import { FreezeDriedBox } from 'features/ECommerce/screens/ProductDetail/FreezeDriedBox'
import { VenisonBox } from 'features/ECommerce/screens/ProductDetail/VenisonBox'
import { VenisonBoxId } from 'models/Box'

type ProductDetailWithConstantSlugs = Omit<ProductDetailType, 'slug'> & {
  slug:
    | '5-lb-beef-patty-recipe'
    | '5-lb-chicken-patty-recipe'
    | '5-lb-venison-patty-recipe'
    | '5-lb-duck-patty-recipe'
    | '5-lb-lamb-patty-recipe'
    | '5-lb-turkey-patty-recipe'
    | 'big-box'
    | 'classic-box'
    | 'small-box'
    | 'freeze-dried-raw-box'
    | 'venison-box'
    | 'startr'
    | 'treats'
}

const patties = [
  '5-lb-chicken-patty-recipe',
  '5-lb-beef-patty-recipe',
  '5-lb-venison-patty-recipe',
  '5-lb-duck-patty-recipe',
  '5-lb-lamb-patty-recipe',
  '5-lb-turkey-patty-recipe',
]

const essentials = ['small-box', 'classic-box', 'big-box']
const treats = 'treats'
const startr = 'startr'
const freezeDriedBoxSlug = 'freeze-dried-raw-box'
const freezeDriedBoxId = 'fd-small-box-chicken'
const venisonBoxSlug = 'venison-box'
// The small variant is used for page metadata. The page itself fetches both variants.
const venisonBoxId = VenisonBoxId.SMALL

export const getServerSideProps = async (ctx: GetServerSidePropsContext) => {
  let { productId } = ctx.query as { productId: string }
  if (productId === 'classic-boxg') {
    return {
      redirect: {
        destination: '/products/classic-box',
        permanent: false,
      },
    }
  }

  if (
    productId === 'fd-small-box-chicken' ||
    productId === 'fd-small-box-beef' ||
    productId === 'fd-small-box-mixed'
  ) {
    return {
      redirect: {
        destination: '/products/freeze-dried-raw-box',
        permanent: false,
      },
    }
  }

  if (productId === VenisonBoxId.SMALL || productId === VenisonBoxId.BIG) {
    return {
      redirect: {
        destination: `/products/${venisonBoxSlug}`,
        permanent: false,
      },
    }
  }

  if (patties.includes(productId)) {
    const proteinType = productId.replace('5-lb-', '').replace('-patty-recipe', '')
    return {
      redirect: {
        destination: `/recipes/${proteinType}`,
        permanent: true,
      },
    }
  }
  let updatedSlug = productId
  // The freeze dried travel box product id (known as slug in products table) does not match the url
  if (productId === freezeDriedBoxSlug) {
    updatedSlug = freezeDriedBoxSlug // updating the slug to the freeze dried box slug while keeping the product lookup successful based on the product table
    productId = freezeDriedBoxId
  }
  // Same for the venison box: the url slug differs from the product variant slug
  if (productId === venisonBoxSlug) {
    updatedSlug = venisonBoxSlug
    productId = venisonBoxId
  }

  const cookies = new Cookies(ctx.req, ctx.res)

  const { dehydratedState, data } = await prefetchProductVariantDetail(productId)

  const authToken = cookies.get(sessionCookieName()) ?? cookies.get(CookieKeys.SESSION)
  const guestId = cookies.get(CookieKeys.GUEST_ID)
  const clientId = authToken ? getPayloadFromJWT(authToken).sub : guestId
  const dehydratedCartState = await prefetchShoppingCart(clientId || '')

  if (
    !patties.includes(productId) &&
    !essentials.includes(productId) &&
    freezeDriedBoxId !== productId &&
    venisonBoxId !== productId &&
    startr !== productId &&
    treats !== productId
  ) {
    return { notFound: true }
  }

  if (
    dehydratedState.queries[0]?.queryKey[1] !== productId &&
    treats !== productId &&
    freezeDriedBoxId !== productId &&
    venisonBoxId !== productId
  ) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      ...dehydratedState,
      metaData: data || '',
      slug: updatedSlug,
      ...dehydratedCartState,
    },
  }
}

export default function ProductDetailPage({
  metaData,
  slug,
}: {
  metaData: ProductDetailWithConstantSlugs
  slug: ProductDetailWithConstantSlugs['slug']
}) {
  const { t } = useTranslation()

  const { pageTitle, title, description } = {
    pageTitle: t(`products:meta.${slug}.page-title`),
    title: t(`products:meta.${slug}.title`),
    description: t(`products:meta.${slug}.description`),
  }

  const canonicalUrl = `https://wefeedraw.com/products/${slug}`

  // prettier-ignore
  const schema = metaData
    ? {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: `${title}`,
        url: `https://wefeedraw.com/products/${slug}`,
        image: `https://wefeedraw.com${metaData.image}`,
        description: description,
        brand: {
          '@type': 'Brand',
          name: 'We Feed Raw',
        },
        offers: {
          '@type': 'Offer',
          url: `https://wefeedraw.com/products/${metaData.slug}`,
          priceCurrency: 'USD',
          price: `${metaData.price.total / 100}`,
          availability: 'https://schema.org/InStock',
          seller: {
            '@type': 'Organization',
            name: 'We Feed Raw',
          },
          shippingDetails: {
            '@type': 'OfferShippingDetails',
            shippingRate: {
              '@type': 'MonetaryAmount',
              value: '0.00',
              currency: 'USD',
            },
          },
          additionalProperty: {
            '@type': 'PropertyValue',
            name: 'Shipping',
            value: 'Free Shipping',
          },
        },
      }
    : {}

  const jsonLdString = JSON.stringify(schema)

  const ProductDetail = () => {
    if (patties.includes(metaData?.slug)) {
      return <FivePound />
    } else if (essentials.includes(metaData?.slug)) {
      return <Essentials />
    } else if (metaData?.slug === (freezeDriedBoxId as string)) {
      return <FreezeDriedBox />
    } else if (slug === venisonBoxSlug) {
      return <VenisonBox />
    } else if (metaData?.slug === startr) {
      return <Startr />
    } else if (slug === treats) {
      return <Treats />
    } else {
      return <FreezeDriedBox />
    }
  }

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta name="title" content={title} />
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
      </Head>
      {metaData && (
        <Script
          id="schema-script"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: jsonLdString }}
        />
      )}
      <ProductDetail />
    </>
  )
}
